'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useQueryConfig } from '@/hooks/queries/useQueryConfig';
import { synthesizeSpeechService } from '@/services/httpServices/SequenceServices';
import type { SynthesizeSpeechServiceConfig } from '@/services/httpServices/SequenceServices/types';

// Global voice player manager to ensure only one audio plays at a time
const globalAudioManager = {
  currentAudio: null as HTMLAudioElement | null,
  currentId: null as string | null,
  abortController: null as AbortController | null,

  // Stop any currently playing audio
  stopCurrent: () => {
    if (globalAudioManager.currentAudio !== null) {
      globalAudioManager.currentAudio.pause();
      globalAudioManager.currentAudio.currentTime = 0;
      globalAudioManager.currentId = null;
    }

    // Abort any in-flight requests
    if (globalAudioManager.abortController !== null) {
      globalAudioManager.abortController.abort();
      globalAudioManager.abortController = null;
    }
  },

  // Set current playing audio
  setCurrentAudio: (audio: HTMLAudioElement, id: string) => {
    globalAudioManager.stopCurrent();
    globalAudioManager.currentAudio = audio;
    globalAudioManager.currentId = id;
  },

  // Create new abort controller
  createAbortController: () => {
    globalAudioManager.abortController = new AbortController();
    return globalAudioManager.abortController;
  },
};

// Audio playback states
export type AudioState = 'idle' | 'loading' | 'playing';

// Create a unique key for the cache
function getVoiceKey(text: string, voiceId: string): string {
  return `voice:${voiceId}:${text}`;
}

/**
 * Hook for handling text-to-speech synthesis and playback
 *
 * @param {object} options Hook options
 * @param {string} options.text Text to synthesize
 * @param {string} options.voiceId Voice ID to use for synthesis
 * @param {string} [options.uniqueId] Optional unique ID for this audio instance
 * @param {boolean} [options.isDisabled] Whether playback is disabled
 * @returns {object} Audio playback controls and state
 */
export default function useAudioPlayback({
  text,
  voiceId,
  uniqueId = `audio-${Math.random().toString(36).substring(2, 9)}`,
  isDisabled = false,
}: {
  text: string;
  voiceId: string;
  uniqueId?: string;
  isDisabled?: boolean;
}) {
  const [audioState, setAudioState] = useState<AudioState>('idle');
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const uniqueIdRef = useRef<string>(uniqueId);
  const queryClient = useQueryClient();
  const { httpRequestConfig } = useQueryConfig();

  // Initialize audio element once
  useEffect(() => {
    const audio = new Audio();

    audio.onplay = () => setAudioState('playing');
    audio.onpause = () => setAudioState('idle');
    audio.onended = () => setAudioState('idle');
    audio.onerror = () => setAudioState('idle');

    audioRef.current = audio;

    // Capture the current unique ID for cleanup
    const currentUniqueId = uniqueIdRef.current;

    return () => {
      // Cleanup: if this component's audio is the current global one, stop it
      if (globalAudioManager.currentId === currentUniqueId) {
        globalAudioManager.stopCurrent();
      }

      audio.pause();
      audio.src = '';
      audio.onplay = null;
      audio.onpause = null;
      audio.onended = null;
      audio.onerror = null;
    };
  }, []);

  // Use React Query mutation for speech synthesis
  const { mutate: synthesizeSpeech, isPending } = useMutation({
    mutationFn: async ({ text, voiceId }: { text: string; voiceId: string }) => {
      globalAudioManager.createAbortController();

      const serviceConfig: SynthesizeSpeechServiceConfig = {
        httpRequestConfig,
        params: {
          params: {
            spaceId: httpRequestConfig.space || '',
          },
          body: {
            voiceId,
            text,
          },
        },
      };

      return await synthesizeSpeechService(serviceConfig);
    },
    onMutate: () => {
      setAudioState('loading');
    },
    onSuccess: async (data, variables) => {
      const audio = audioRef.current;
      if (audio === null) {
        return;
      }

      // Cache the URL in the query client
      const key = getVoiceKey(variables.text, variables.voiceId);
      queryClient.setQueryData([key], data.data.url);

      try {
        // Set as current global audio
        globalAudioManager.setCurrentAudio(audio, uniqueIdRef.current);

        // Setup and play audio
        audio.src = data.data.url;
        audio.load();
        await audio.play();
        setAudioState('playing');
      } catch (error) {
        console.error('Failed to play audio:', error);
        setAudioState('idle');
      }
    },
    onError: (error) => {
      console.error('Synthesis error:', error);
      setAudioState('idle');
    },
  });

  // Play/pause toggle
  const togglePlay = useCallback(async () => {
    if (isDisabled === true || text === '') {
      return;
    }

    const audio = audioRef.current;
    if (audio === null) {
      return;
    }

    // If this audio is currently playing, pause it
    if (audioState === 'playing' && globalAudioManager.currentId === uniqueIdRef.current) {
      audio.pause();
      return;
    }

    // Check if we already have the URL cached in React Query
    const cacheKey = getVoiceKey(text, voiceId);
    const cachedUrl = queryClient.getQueryData<string>([cacheKey]);

    if (cachedUrl !== undefined) {
      try {
        // Stop any other playing audio
        globalAudioManager.stopCurrent();

        // Set as current global audio
        globalAudioManager.setCurrentAudio(audio, uniqueIdRef.current);

        // Use cached URL
        audio.src = cachedUrl;
        audio.load();
        await audio.play();
        setAudioState('playing');
      } catch (error) {
        console.error('Failed to play cached audio:', error);
        setAudioState('idle');

        // If there's an error with cached URL, remove it and retry
        queryClient.removeQueries({ queryKey: [cacheKey] });
        synthesizeSpeech({ text, voiceId });
      }
    } else {
      // No cached URL, fetch new audio
      synthesizeSpeech({ text, voiceId });
    }
  }, [isDisabled, text, voiceId, audioState, queryClient, synthesizeSpeech]);

  // Force stop any playback
  const stopPlayback = useCallback(() => {
    if (globalAudioManager.currentId === uniqueIdRef.current) {
      globalAudioManager.stopCurrent();
      setAudioState('idle');
    }
  }, []);

  return {
    audioState,
    isLoading: isPending,
    togglePlay,
    stopPlayback,
    isPlaying: audioState === 'playing',
  };
}
